import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, IconButton, Switch,
} from '@mui/material';
import {
  Close, SkipNext, SkipPrevious, Shuffle, QueueMusic, Speaker, LibraryMusic, Lyrics,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useOverlayBackButton } from '../../hooks/useOverlayBackButton';
import {
  SonosSpeaker, isSonosPlaying, setPlayMode, notifyQueueReordered, areGroupVolumesNormalized,
  prefetchQueue, fetchTrackLyrics, TrackLyricsResponse,
} from '../../services/sonosApi';
import MinimalVolumeBar from './MinimalVolumeBar';
import CircularProgressPlayButton from './CircularProgressPlayButton';
import AlbumArtImage from './AlbumArtImage';
import RepeatModeButton from './RepeatModeButton';
import SonosQueueOverlay from './SonosQueueOverlay';
import LyricsOverlay from './LyricsOverlay';
import { parsePlayMode, RepeatMode } from './sonosPlayMode';
import TrackProgressSlider from './TrackProgressSlider';
import PlayingSourceLabel from './PlayingSourceLabel';
import { parseSonosTime } from './sonosPlaybackUtils';
import { COMPACT_PLAYER_H, COMPACT_PLAYER_BOTTOM } from './CompactPlayerBar';
import {
  modalSheetHandleBarSx,
  modalSheetHandleSx,
  AMBIENT_GLOW,
  modalSheetShellSx,
  SONOS_BLUE,
  sonosIconTapSx,
} from './sonosUiStyles';

interface ExpandedPlayerSheetProps {
  open: boolean;
  onClose: () => void;
  /** Cierre "total": oculta también el reproductor compacto. Usado cuando no hay
   *  reproducción activa y el usuario arrastra la hoja hacia abajo. */
  onDismiss?: () => void;
  /** true si hay reproducción activa: al arrastrar hacia abajo colapsa al
   *  compacto en lugar de cerrar todo. */
  playbackActive?: boolean;
  coordinator: SonosSpeaker | null;
  destinationSpeakers: SonosSpeaker[];
  canControl: boolean;
  crossfade: boolean;
  onCrossfadeChange: (v: boolean) => void;
  /** false en reproducción local (SDK no soporta fundido). Default true. */
  showCrossfadeControl?: boolean;
  onTransport: (action: 'play' | 'pause' | 'previous' | 'next') => void;
  onSeek: (seconds: number) => void;
  onOpenDestination: () => void;
  onVolumeApplied: (speakerId: number, volume: number, mute: boolean, groupSpeakerIds?: number[]) => void;
  playingSourceLabel?: string | null;
  embedded?: boolean;
  showPlaylistsButton?: boolean;
  onOpenPlaylists?: () => void;
  albumArtScale?: number;
  showPlaybackMedia?: boolean;
  /** Solo dock landscape: tipografía/espaciado más compactos. No afecta vertical. */
  dense?: boolean;
  /**
   * Controles al fondo del panel y arte/título centrados en el hueco.
   * La letra, si está abierta, ocupa todo ese hueco. Vista horizontal.
   */
  anchorControls?: boolean;
  /**
   * Aviso al padre antes de abrir la cola (cerrar playlist / dónde sonar).
   * La cola la abre siempre este sheet (overlay propio), para evitar titileos
   * por re-render del tab padre.
   */
  onQueueWillOpen?: () => void;
  /**
   * Panel de cola a mostrar en el overlay (p. ej. LocalQueuePanel en «este dispositivo»).
   * Si no se pasa, se usa la cola Sonos del parlante.
   */
  queuePanelOverride?: React.ReactNode;
}

const ExpandedPlayerSheet: React.FC<ExpandedPlayerSheetProps> = ({
  open,
  onClose,
  onDismiss,
  playbackActive = false,
  coordinator,
  destinationSpeakers,
  canControl,
  crossfade,
  onCrossfadeChange,
  showCrossfadeControl = true,
  onTransport,
  onSeek,
  onOpenDestination,
  onVolumeApplied,
  playingSourceLabel,
  embedded = false,
  showPlaylistsButton = false,
  onOpenPlaylists,
  albumArtScale = 1,
  showPlaybackMedia = true,
  dense = false,
  anchorControls = false,
  onQueueWillOpen,
  queuePanelOverride,
}) => {
  const { t } = useTranslation();
  const docked = dense || anchorControls;
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState<RepeatMode>('off');
  const [queueOpen, setQueueOpen] = useState(false);
  const [volumeLayoutPerDevice, setVolumeLayoutPerDevice] = useState(false);
  const [lyricsOpen, setLyricsOpen] = useState(false);
  const [lyricsLoading, setLyricsLoading] = useState(false);
  const [lyricsData, setLyricsData] = useState<TrackLyricsResponse | null>(null);
  const [lyricsError, setLyricsError] = useState<'not_found' | 'network' | null>(null);
  const lyricsCacheRef = useRef<{ key: string; data: TrackLyricsResponse } | null>(null);
  const seekIgnoreUntilRef = useRef(0);
  const lastCoordinatorRef = useRef(coordinator);
  if (coordinator) lastCoordinatorRef.current = coordinator;
  const stableCoordinator = coordinator ?? lastCoordinatorRef.current;
  const playing = isSonosPlaying(stableCoordinator?.playback_state);

  // Cierre enrutado: con reproducción activa colapsa al compacto (onClose);
  // sin reproducción cierra todo (onDismiss). Si hay letra abierta, la cierra primero.
  const closeRouted = useCallback(() => {
    if (lyricsOpen) {
      setLyricsOpen(false);
      return;
    }
    if (playbackActive) onClose();
    else (onDismiss ?? onClose)();
  }, [lyricsOpen, playbackActive, onClose, onDismiss]);
  const requestClose = useOverlayBackButton(
    open && !embedded,
    closeRouted,
    'sonos-expanded-player',
  );

  // --- Animación de hoja (solo modo no-embebido) --------------------------
  // La tarjeta "crece" desde la altura del compacto (borde inferior fijo) y se
  // cierra deslizándola desde el agarre superior.
  const [mounted, setMounted] = useState(false);
  const [grown, setGrown] = useState(false);     // top:0 (abierta) vs colapsada
  const [leavingDown, setLeavingDown] = useState(false); // salida deslizando abajo
  const [dragY, setDragY] = useState(0);          // arrastre del dedo (px, >=0)
  const [dragging, setDragging] = useState(false);
  const gripDragRef = useRef<{ y: number; id: number } | null>(null);

  const collapsedTop = `calc(100% - ${COMPACT_PLAYER_H + COMPACT_PLAYER_BOTTOM}px)`;

  useEffect(() => {
    if (embedded) return undefined;
    if (open) {
      setMounted(true);
      setLeavingDown(false);
      // Un solo frame: montar ya expandido (evita flash colapsado→lleno).
      setGrown(true);
      return undefined;
    }
    if (mounted) {
      // Al cerrar: si hay reproducción, colapsa a la altura del compacto;
      // si no, desliza toda la tarjeta hacia abajo.
      if (playbackActive) setGrown(false);
      else setLeavingDown(true);
      const tmo = window.setTimeout(() => {
        setMounted(false);
        setGrown(false);
        setLeavingDown(false);
        setDragY(0);
        setQueueOpen(false);
      }, 320);
      return () => window.clearTimeout(tmo);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, embedded]);

  const onGripDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    gripDragRef.current = { y: e.clientY, id: e.pointerId };
    setDragging(true);
  };
  const onGripMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = gripDragRef.current;
    if (!d || d.id !== e.pointerId) return;
    setDragY(Math.max(0, e.clientY - d.y));
  };
  const onGripEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = gripDragRef.current;
    if (!d || d.id !== e.pointerId) return;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    const dy = e.clientY - d.y;
    gripDragRef.current = null;
    setDragging(false);
    if (dy > 80) requestClose();
    else setDragY(0);
  };

  useEffect(() => {
    if (open && coordinator?.id) {
      prefetchQueue(coordinator.id);
    }
  }, [open, coordinator?.id]);

  useEffect(() => {
    if (!open) setLyricsOpen(false);
  }, [open]);

  useEffect(() => {
    if (!lyricsOpen || !coordinator?.current_title) {
      return undefined;
    }
    const speakerId = coordinator.id;
    const isLocalPlayer = speakerId == null || speakerId <= 0;
    const durationSec = parseSonosTime(coordinator.track_duration) ?? 0;
    const cacheKey = [
      isLocalPlayer ? 'local' : String(speakerId),
      coordinator.current_title,
      coordinator.current_artist || '',
      coordinator.current_album || '',
      String(durationSec),
    ].join('|');
    if (lyricsCacheRef.current?.key === cacheKey) {
      setLyricsData(lyricsCacheRef.current.data);
      setLyricsError(null);
      setLyricsLoading(false);
      return undefined;
    }
    const ac = new AbortController();
    setLyricsLoading(true);
    setLyricsError(null);
    // Sonos: metadata desde el parlante. Local (id fake ≤0): metadata del Web Player.
    const request = isLocalPlayer
      ? fetchTrackLyrics({
          track_name: coordinator.current_title,
          artist_name: coordinator.current_artist || '',
          album_name: coordinator.current_album || undefined,
          duration: durationSec,
        })
      : fetchTrackLyrics({ speaker_id: speakerId });
    void request
      .then((data) => {
        if (ac.signal.aborted) return;
        lyricsCacheRef.current = { key: cacheKey, data };
        setLyricsData(data);
        setLyricsError(null);
      })
      .catch((err: { response?: { status?: number } }) => {
        if (ac.signal.aborted) return;
        setLyricsData(null);
        setLyricsError(err?.response?.status === 404 ? 'not_found' : 'network');
      })
      .finally(() => {
        if (!ac.signal.aborted) setLyricsLoading(false);
      });
    return () => { ac.abort(); };
  }, [
    lyricsOpen,
    coordinator?.id,
    coordinator?.current_title,
    coordinator?.current_artist,
    coordinator?.current_album,
    coordinator?.track_duration,
  ]);

  const openQueue = () => {
    // En «este dispositivo» el id es -1: no hay cola Sonos que prefetch.
    if (coordinator?.id != null && coordinator.id > 0) prefetchQueue(coordinator.id);
    onQueueWillOpen?.();
    setQueueOpen(true);
  };
  const destCount = destinationSpeakers.length;
  const deviceLabel = destCount === 0
    ? '—'
    : destinationSpeakers.map((s) => s.nombre).join(' + ');
  const unifiedGroupIds = destCount >= 2 ? destinationSpeakers.map((s) => s.id) : undefined;
  const perDeviceVolumes = volumeLayoutPerDevice;
  const volBarCount = perDeviceVolumes ? destCount : 1;
  const artShrink = Math.max(0, volBarCount - 1) * Math.round(26 * albumArtScale);
  const artBase = Math.round(230 * albumArtScale);
  const artMin = Math.round(88 * albumArtScale);
  const artVh = (dense ? 36 : 44) * albumArtScale;
  const artSizeCss = `min(${Math.max(artMin, artBase - artShrink)}px, calc(${artVh}vh - ${artShrink}px))`;
  const volumeSpeakers = perDeviceVolumes ? [...destinationSpeakers].reverse() : [];
  const needsVolumeScroll = perDeviceVolumes && volBarCount >= 2;

  useEffect(() => {
    if (!open && !embedded) return;
    setVolumeLayoutPerDevice(
      destCount >= 2 && !areGroupVolumesNormalized(destinationSpeakers),
    );
  }, [open, embedded, destCount, destinationSpeakers]);

  useEffect(() => {
    const parsed = parsePlayMode(coordinator?.play_mode);
    setShuffle(parsed.shuffle);
    setRepeat(parsed.repeat);
  }, [coordinator?.play_mode, coordinator?.id]);

  const basePosition = useMemo(
    () => parseSonosTime(coordinator?.track_position),
    [coordinator?.track_position],
  );
  const durationSec = useMemo(
    () => parseSonosTime(coordinator?.track_duration),
    [coordinator?.track_duration],
  );
  const [livePosition, setLivePosition] = useState<number | null>(basePosition);

  useEffect(() => {
    if (Date.now() < seekIgnoreUntilRef.current) return;
    setLivePosition(basePosition);
  }, [basePosition, coordinator?.current_title]);

  useEffect(() => {
    if (!playing || livePosition == null) return undefined;
    const id = window.setInterval(() => {
      setLivePosition((prev) => (prev == null ? prev : prev + 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [playing, livePosition, durationSec]);

  const handleVolume = (volume: number, mute: boolean) => {
    if (!coordinator) return;
    onVolumeApplied(coordinator.id, volume, mute, unifiedGroupIds);
  };

  const handleDeviceVolume = (speakerId: number) => (volume: number, mute: boolean) => {
    onVolumeApplied(speakerId, volume, mute);
  };

  const handleSeek = (sec: number) => {
    seekIgnoreUntilRef.current = Date.now() + 12000;
    setLivePosition(sec);
    onSeek(sec);
  };

  const applyPlayMode = async (nextShuffle: boolean, nextRepeat: RepeatMode) => {
    if (!coordinator || !canControl) return;
    const prevShuffle = shuffle;
    const prevRepeat = repeat;
    setShuffle(nextShuffle);
    setRepeat(nextRepeat);
    try {
      const res = await setPlayMode(coordinator.id, { shuffle: nextShuffle, repeat: nextRepeat });
      // Aleatorio SHC: el backend mezcló la cola del Sonos → mostrar el orden nuevo.
      if (res?.reordered) notifyQueueReordered([coordinator.id]);
    } catch {
      setShuffle(prevShuffle);
      setRepeat(prevRepeat);
    }
  };

  const toggleShuffle = () => { void applyPlayMode(!shuffle, repeat); };

  const handleRepeatChange = (mode: RepeatMode) => { void applyPlayMode(shuffle, mode); };

  if (!stableCoordinator) return null;

  const playerShell = (
    <Box
      data-audio-no-swipe=""
      sx={{
        position: 'relative',
        flex: 1,
        minHeight: 0,
        width: '100%',
        height: '100%',
        backgroundColor: '#000',
        backgroundImage: AMBIENT_GLOW,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {!embedded && (
        <Box sx={{ ...modalSheetHandleSx, position: 'relative' }}>
          <Box
            data-audio-player-handle=""
            onPointerDown={onGripDown}
            onPointerMove={onGripMove}
            onPointerUp={onGripEnd}
            onPointerCancel={onGripEnd}
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              touchAction: 'none',
            }}
          >
            <Box sx={modalSheetHandleBarSx} />
          </Box>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              requestClose();
            }}
            size="small"
            disableRipple
            aria-label={t('common.close') as string}
            sx={{ position: 'absolute', right: 4, top: 1, color: '#999', p: 0.2, zIndex: 1, ...sonosIconTapSx }}
          >
            <Close sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      )}

      <Box sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        px: dense ? 1.25 : 2,
        pb: dense ? 1 : 1.5,
        pt: embedded ? (dense ? 0.75 : 0.5) : 0,
        overflowY: needsVolumeScroll ? 'auto' : 'hidden',
        WebkitOverflowScrolling: needsVolumeScroll ? 'touch' : undefined,
      }}>
        <Box sx={docked ? {
          // Arte y título centrados en el hueco; la letra lo ocupa entero. Tiempo y controles al fondo.
          flexGrow: 1,
          flexShrink: 0,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        } : { flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
          <Box sx={docked ? {
            position: 'relative',
            flexGrow: 1,
            flexShrink: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          } : { position: 'relative', flexShrink: 0 }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: dense ? 1.25 : 2,
                flexShrink: 0,
                mt: 0,
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: '-18%',
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(35,145,255,0.32) 0%, transparent 68%)',
                  pointerEvents: 'none',
                  zIndex: 0,
                },
              }}>
                <AlbumArtImage
                  speakerId={stableCoordinator.id}
                  artUrlKey={stableCoordinator.current_album_art_url}
                  titleKey={stableCoordinator.current_title}
                  artistKey={stableCoordinator.current_artist}
                  fetchArt={showPlaybackMedia}
                  size={artSizeCss}
                  borderRadius={3}
                  sx={{
                    position: 'relative',
                    zIndex: 1,
                    width: artSizeCss,
                    height: artSizeCss,
                    maxHeight: artSizeCss,
                    boxShadow: '0 8px 28px rgba(0,0,0,0.45), 0 0 32px rgba(35,145,255,0.18)',
                    transition: 'width 0.2s ease, height 0.2s ease',
                  }}
                />
              </Box>

              <Typography sx={{
                color: '#fff',
                fontSize: dense ? 16 : 20,
                fontWeight: 800,
                lineHeight: 1.2,
                mb: 0.35,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: dense ? 'nowrap' : 'normal',
              }}>
                {showPlaybackMedia ? (stableCoordinator.current_title || '') : ''}
              </Typography>
              <Typography sx={{
                color: '#aaa',
                fontSize: dense ? 12 : 13,
                mb: dense ? 0.5 : 0.75,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: dense ? 'nowrap' : 'normal',
              }}>
                {showPlaybackMedia
                  ? ([stableCoordinator.current_artist, stableCoordinator.current_album].filter(Boolean).join(' • ') || '')
                  : ''}
              </Typography>

              {playingSourceLabel && (
                <PlayingSourceLabel
                  label={playingSourceLabel}
                  variant="expanded"
                  sx={{ mb: 0.85 }}
                />
              )}

              <LyricsOverlay
                open={lyricsOpen}
                onClose={() => setLyricsOpen(false)}
                loading={lyricsLoading}
                error={lyricsError}
                instrumental={Boolean(lyricsData?.instrumental)}
                plainLyrics={lyricsData?.plain_lyrics ?? null}
                syncedLines={lyricsData?.synced_lines ?? []}
                livePositionSec={livePosition}
              />
          </Box>

              <TrackProgressSlider
                position={livePosition}
                duration={durationSec}
                disabled={!canControl}
                onSeek={handleSeek}
              />
          </Box>

          <Box sx={{ flexShrink: 0, pt: 0.75 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', my: 0.75, px: 0.5 }}>
                <IconButton
                  onClick={toggleShuffle}
                  disabled={!canControl}
                  disableRipple
                  sx={{
                    color: shuffle ? SONOS_BLUE : '#aaa',
                    ...(shuffle ? {
                      bgcolor: 'rgba(35,145,255,0.12)',
                      '&:active': { bgcolor: 'rgba(35,145,255,0.28)', transform: 'scale(0.93)' },
                    } : sonosIconTapSx),
                  }}
                >
                  <Shuffle />
                </IconButton>
                <IconButton
                  disabled={!canControl}
                  onClick={() => onTransport('previous')}
                  disableRipple
                  sx={{ color: '#fff', ...sonosIconTapSx }}
                >
                  <SkipPrevious sx={{ fontSize: 30 }} />
                </IconButton>
                <CircularProgressPlayButton
                  playing={playing}
                  progress={durationSec && livePosition != null ? livePosition / durationSec : 0}
                  disabled={!canControl}
                  size={58}
                  onClick={() => onTransport(playing ? 'pause' : 'play')}
                />
                <IconButton
                  disabled={!canControl}
                  onClick={() => onTransport('next')}
                  disableRipple
                  sx={{ color: '#fff', ...sonosIconTapSx }}
                >
                  <SkipNext sx={{ fontSize: 30 }} />
                </IconButton>
                <RepeatModeButton
                  mode={repeat}
                  disabled={!canControl}
                  onChange={handleRepeatChange}
                />
              </Box>

              {perDeviceVolumes ? (
                <Box sx={{ display: 'flex', flexDirection: 'column-reverse', gap: 0.15, mb: 0.5 }}>
                  {volumeSpeakers.map((sp) => (
                    <MinimalVolumeBar
                      key={sp.id}
                      speakerId={sp.id}
                      volume={sp.volume ?? 0}
                      mute={sp.mute}
                      disabled={!canControl}
                      label={sp.nombre}
                      onVolumeApplied={handleDeviceVolume(sp.id)}
                    />
                  ))}
                </Box>
              ) : (
                <MinimalVolumeBar
                  speakerId={stableCoordinator.id}
                  volume={stableCoordinator.volume ?? 0}
                  mute={stableCoordinator.mute}
                  disabled={!canControl}
                  volumeTargetIds={unifiedGroupIds}
                  onVolumeApplied={handleVolume}
                />
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5, gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  <IconButton onClick={openQueue} disableRipple sx={{ color: '#fff', ...sonosIconTapSx }}>
                    <QueueMusic />
                  </IconButton>
                  <IconButton
                    onClick={() => setLyricsOpen((v) => !v)}
                    disabled={!stableCoordinator?.current_title}
                    disableRipple
                    aria-label={t('musicSystem.player.lyrics.button') as string}
                    sx={{
                      color: !stableCoordinator?.current_title
                        ? '#555'
                        : lyricsOpen
                          ? SONOS_BLUE
                          : '#fff',
                      opacity: !stableCoordinator?.current_title ? 0.5 : 1,
                      ...(lyricsOpen ? {
                        bgcolor: 'rgba(35,145,255,0.12)',
                        borderRadius: 1,
                        '&:active': { bgcolor: 'rgba(35,145,255,0.28)', transform: 'scale(0.93)' },
                      } : sonosIconTapSx),
                    }}
                  >
                    <Lyrics />
                  </IconButton>
                  {showPlaylistsButton && (
                    <IconButton onClick={onOpenPlaylists} disableRipple sx={{ color: '#fff', ...sonosIconTapSx }}>
                      <LibraryMusic />
                    </IconButton>
                  )}
                </Box>

                <Box
                  onClick={onOpenDestination}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 0.75, flex: 1, justifyContent: 'center', minWidth: 0,
                    cursor: 'pointer', borderRadius: 1, py: 0.25, px: 0.5,
                    transition: 'background-color 0.1s ease',
                    WebkitTapHighlightColor: 'transparent',
                    '&:active': { bgcolor: 'rgba(35,145,255,0.18)' },
                  }}
                >
                  <Box sx={{ position: 'relative' }}>
                    <Speaker sx={{ color: '#fff', fontSize: 22 }} />
                    {destCount > 0 && (
                      <Box sx={{
                        position: 'absolute', top: -4, right: -8,
                        width: 14, height: 14, borderRadius: '50%',
                        backgroundColor: SONOS_BLUE, color: '#fff', fontSize: 9, fontWeight: 700,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {destCount}
                      </Box>
                    )}
                  </Box>
                  <Typography sx={{ color: '#fff', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {deviceLabel}
                  </Typography>
                </Box>

                {showCrossfadeControl && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography
                      onClick={() => onCrossfadeChange(!crossfade)}
                      sx={{ color: '#999', fontSize: 10, cursor: 'pointer', userSelect: 'none' }}
                    >
                      {t('musicSystem.config.crossfadeShort')}
                    </Typography>
                    <Switch
                      size="small"
                      checked={crossfade}
                      disabled={!canControl}
                      onChange={(e) => onCrossfadeChange(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: SONOS_BLUE },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: SONOS_BLUE },
                      }}
                    />
                  </Box>
                )}
              </Box>
          </Box>
      </Box>
    </Box>
  );

  const localQueueOverlay = (
    <SonosQueueOverlay
      open={queueOpen}
      onClose={() => setQueueOpen(false)}
      speakerId={stableCoordinator.id}
      coordinator={stableCoordinator}
      canControl={canControl}
      shuffle={shuffle}
      onShuffleChange={(v) => { void applyPlayMode(v, repeat); }}
      repeat={repeat}
      onRepeatChange={(mode) => { void applyPlayMode(shuffle, mode); }}
      panelOverride={queuePanelOverride}
    />
  );

  if (embedded) {
    return (
      <>
        <Box sx={{
          position: 'relative',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {playerShell}
        </Box>
        {localQueueOverlay}
      </>
    );
  }

  return (
    <>
      {mounted && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: grown ? 5 : collapsedTop,
            bottom: COMPACT_PLAYER_BOTTOM,
            zIndex: 50,
            boxSizing: 'border-box',
            width: '100%',
            ...modalSheetShellSx,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            transform: leavingDown ? 'translateY(110%)' : `translateY(${dragY}px)`,
            transition: dragging
              ? 'none'
              : 'top 300ms cubic-bezier(0.22,0.61,0.36,1), transform 300ms cubic-bezier(0.22,0.61,0.36,1)',
            willChange: 'top, transform',
          }}
        >
          {playerShell}
        </Box>
      )}
      {localQueueOverlay}
    </>
  );
};

export default ExpandedPlayerSheet;
